import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {CoreComponent} from './modules/core/core.component';
import {DictionaryResolver} from './modules/core/resolvers/dictionary.resolver';
import {AuthGuard} from './guards/auth.guard';
import {DocumentListComponent} from './modules/shared/pages/document-list';
import {NavItemCodes} from './enums/nav-item-codes.enum';
import {DictionaryCode} from './enums/dictionary-code.enum';
import {DevelopmentGuard} from './guards/development.guard';
import {ForbiddenComponent} from './modules/core/components/forbidden/forbidden.component';
import {AclGuard} from './guards/acl.guard';
import {PermissionArea} from './enums/permission-area.enum';
import {ActiveProductsResolver} from './modules/core/resolvers/active-products.resolver';
import {SystemUser} from './models/system-user.model';

const getAdminBreadcrumb = (currentUser: SystemUser) => {
    return currentUser?.role?.code !== 'ADMIN_PARTNER'
        ? NavItemCodes.MANAGEMENT
        : null;
};

const routes: Routes = [
    {
        path: 'styleguide',
        loadChildren: './modules/styleguide/styleguide.module#StyleguideModule',
        canActivate: [DevelopmentGuard],
        data: {
            breadcrumb: null
        }
    },
    {
        path: 'login',
        loadChildren: './modules/auth/auth.module#AuthModule',
        data: {
            breadcrumb: null
        }
    },
    {
        path: '',
        component: CoreComponent,
        canActivate: [AuthGuard],
        data: {
            breadcrumb: NavItemCodes.HOME,
        },
        resolve: {
            products: ActiveProductsResolver,
        },
        children: [
            {
                path: 'start',
                loadChildren: './modules/start/start.module#StartModule',
                data: {
                    breadcrumb: null
                }
            },
            {
                path: 'admin',
                loadChildren: './modules/admin/admin.module#AdminModule',
                data: {
                    breadcrumb: getAdminBreadcrumb,
                    permissionArea: PermissionArea.MANAGEMENT,
                },
                canActivate: [AclGuard]
            },
            {
                path: 'sales/cpi',
                canActivate: [AclGuard],
                loadChildren: './modules/sales-cpi/sales-cpi.module#SalesCpiModule',
                data: {
                    breadcrumb: null,
                    permissionArea: [PermissionArea.SALES_CPI, PermissionArea.POLICY_REGISTRY],
                },
            },
            {
                path: 'sales/pg',
                canActivate: [AclGuard],
                loadChildren: './modules/sales-pg/sales-pg.module#SalesPgModule',
                data: {
                    breadcrumb: null,
                    permissionArea: [PermissionArea.SALES_UPG, PermissionArea.POLICY_REGISTRY],
                },
            },
            {
                path: 'documents',
                canActivate: [AclGuard],
                data: {
                    breadcrumb: NavItemCodes.DOCUMENT_VIEW,
                    permissionArea: PermissionArea.DOCUMENTS_VIEW,
                    dictionaries: [DictionaryCode.DOCUMENT_CATEGORY],
                },
                resolve: {
                    dictionaries: DictionaryResolver,
                },
                component: DocumentListComponent,
            },
            {
                path: 'policy-registry',
                canActivate: [AclGuard],
                loadChildren: './modules/policy/policy.module#PolicyModule',
                data: {
                    breadcrumb: NavItemCodes.POLICY_LABEL_SHORT,
                    permissionArea: PermissionArea.POLICY_REGISTRY,
                }
            },
            {
                path: 'reports',
                canActivate: [AclGuard],
                loadChildren: './modules/reports/reports.module#ReportsModule',
                data: {
                    permissionArea: [PermissionArea.MARKETING_REPORT, PermissionArea.USERS_REPORT],
                    breadcrumb: NavItemCodes.REPORTS_LIST,
                }
            },
            {
                path: 'migrated-policies',
                canActivate: [AclGuard],
                loadChildren: './modules/migrated-policies/migrated-policies.module#MigratedPoliciesModule',
                data: {
                    permissionArea: PermissionArea.MIGRATED_POLICY_REGISTRY,
                    breadcrumb: NavItemCodes.MIGRATED_POLICIES_LABEL_SHORT,
                }
            },
            {
                path: 'repository-policies',
                canActivate: [AclGuard],
                loadChildren: './modules/repository/repository-policies.module#RepositoryPoliciesModule',
                data: {
                    permissionArea: PermissionArea.REPOSITORY_POLICIES_REGISTRY,
                    breadcrumb: NavItemCodes.REPOSITORY_LABEL_SHORT,
                }
            },
            {
                path: 'forbidden',
                component: ForbiddenComponent,
            },
            {path: '**', redirectTo: 'start'},
        ]
    },
    {path: '**', redirectTo: 'login'},
];

@NgModule({
    imports: [RouterModule.forRoot(routes, {scrollOffset: [0, 0], scrollPositionRestoration: 'top'})],
    exports: [RouterModule],
    providers: [DictionaryResolver, AclGuard, ActiveProductsResolver],
})
export class AppRoutingModule {
}
